import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Quote, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const REVIEW_URL = "https://www.google.com/maps/search/?api=1&query=Lopez+Appliances+San+Francisco";

function Stars({ count = 5 }) {
  return (
    <div className="flex items-center gap-1" aria-label={`${count} stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <motion.span
          key={i}
          initial={{ scale: 0, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: i * 0.08, duration: 0.4, ease: "easeOut" }}
        >
          <Star
            size={16}
            className={i < count ? "fill-[#ffd700] text-[#ffd700]" : "text-white/20"}
            strokeWidth={1.4}
          />
        </motion.span>
      ))}
    </div>
  );
}

export default function Reviews() {
  const { t, lang } = useLanguage();
  const list = t("reviews.list") || [];
  const [idx, setIdx] = useState(0);

  // Reset index if language changes (different array reference)
  useEffect(() => setIdx(0), [lang]);

  useEffect(() => {
    if (!list.length) return;
    const id = setInterval(() => setIdx((v) => (v + 1) % list.length), 7000);
    return () => clearInterval(id);
  }, [list.length]);

  const next = () => setIdx((v) => (v + 1) % list.length);
  const prev = () => setIdx((v) => (v - 1 + list.length) % list.length);
  const review = list[idx];

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="relative section-pad bg-[#0a0a0a]"
    >
      <div className="absolute inset-x-0 top-0 gold-divider" />
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="text-overline">{t("reviews.overline")}</span>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3">
            <span className="gold-gradient-text italic">
              {t("reviews.title")}
            </span>
          </h2>
          <p
            data-testid="reviews-summary"
            className="mt-4 text-sm tracking-[0.2em] uppercase text-white/60"
          >
            {t("reviews.summary")}
          </p>
        </div>

        <div className="relative">
          <div className="card-surface p-8 md:p-14 min-h-[320px] md:min-h-[280px] gold-frame">
            <Quote
              size={56}
              className="text-[#d4af37]/25 absolute top-6 left-6"
              strokeWidth={1}
            />
            <AnimatePresence mode="wait">
              {review && (
                <motion.blockquote
                  key={`${lang}-${idx}`}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -18 }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  data-testid={`review-card-${idx}`}
                  className="relative max-w-3xl mx-auto text-center"
                >
                  <Stars count={review.stars} />
                  <p className="font-serif-display italic text-lg md:text-2xl leading-relaxed text-white/90 mt-6">
                    “{review.text}”
                  </p>
                  <footer className="mt-7">
                    <p className="text-[#d4af37] tracking-[0.15em] uppercase text-xs font-semibold">
                      {review.name}
                    </p>
                    {review.tag && (
                      <p className="text-[10px] tracking-[0.3em] uppercase text-white/45 mt-1">
                        {review.tag}
                      </p>
                    )}
                  </footer>
                </motion.blockquote>
              )}
            </AnimatePresence>
          </div>

          <div className="flex items-center justify-center gap-6 mt-8">
            <button
              data-testid="reviews-prev"
              onClick={prev}
              className="w-11 h-11 rounded-full border border-[rgba(212,175,55,0.45)] text-[#d4af37] hover:bg-[rgba(212,175,55,0.12)] hover:shadow-[0_0_18px_rgba(212,175,55,0.35)] transition-all flex items-center justify-center"
              aria-label="Previous review"
            >
              <ChevronLeft size={18} />
            </button>
            <div className="flex items-center gap-2">
              {list.map((_, i) => (
                <button
                  key={i}
                  data-testid={`reviews-dot-${i}`}
                  onClick={() => setIdx(i)}
                  className={`h-1.5 rounded-full transition-all ${
                    i === idx ? "w-8 bg-[#d4af37]" : "w-1.5 bg-white/25"
                  }`}
                  aria-label={`Go to review ${i + 1}`}
                />
              ))}
            </div>
            <button
              data-testid="reviews-next"
              onClick={next}
              className="w-11 h-11 rounded-full border border-[rgba(212,175,55,0.45)] text-[#d4af37] hover:bg-[rgba(212,175,55,0.12)] hover:shadow-[0_0_18px_rgba(212,175,55,0.35)] transition-all flex items-center justify-center"
              aria-label="Next review"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          <div className="flex justify-center mt-10">
            <a
              href={REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="leave-review-cta"
              className="btn-outline-gold"
            >
              <ExternalLink size={14} />
              {t("reviews.cta")}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
